"use client"

import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Progress } from "@/components/ui/progress"
import { ScrollArea } from "@/components/ui/scroll-area"
import { TooltipProvider } from "@/components/ui/tooltip"
import {
  Plus,
  AlertCircle,
  ImageIcon,
  Layers,
  Settings2,
  X,
  Download,
  GripVertical,
} from "lucide-react"
import { toast } from "sonner"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { useFileUpload, useKeyboardShortcuts, type UploadedFile } from "@/lib/hooks"
import { KeyboardShortcutsDialog } from "@/components/keyboard-shortcuts-dialog"
import {
  PageCanvas,
  PageList,
  PropertiesPanel,
  WorkspaceToolbar,
  WorkspaceSettingsTab,
  type ImageItem,
  type PageConfig,
  type PageSize,
  type Orientation,
  type Position,
  type Size,
  PAGE_DIMENSIONS,
} from "@/components/workspace"
import { API_URL, API_KEY } from "@/lib/api-config"

type ProcessingStatus = "idle" | "creating-job" | "uploading" | "processing" | "completed" | "error"

interface UploadSlot {
  index: number
  uploadUrl: string
  storageKey: string
}

interface HistoryState {
  pages: PageConfig[]
  selectedPageIndex: number
}

const generateId = () => Math.random().toString(36).substring(2, 11)

const NUDGE_AMOUNT = 1
const NUDGE_AMOUNT_SHIFT = 10

export function ImageToPdfWorkspace() {
  const [pages, setPages] = useState<PageConfig[]>([createEmptyPage("A4", "portrait")])
  const [selectedPageIndex, setSelectedPageIndex] = useState(0)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)

  const [pageSize, setPageSize] = useState<PageSize>("A4")
  const [orientation, setOrientation] = useState<Orientation>("portrait")
  const [margins, setMargins] = useState(20)
  const [showGrid, setShowGrid] = useState(false)
  const [canvasScale, setCanvasScale] = useState(0.8)
  const [outputFileName, setOutputFileName] = useState("document")

  const [status, setStatus] = useState<ProcessingStatus>("idle")
  const [uploadProgress, setUploadProgress] = useState(0)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null)
  const [resultInfo, setResultInfo] = useState<{ fileName: string; size: number; pages: number } | null>(null)
  const [error, setError] = useState<string | null>(null)

  const [history, setHistory] = useState<HistoryState[]>([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [showShortcuts, setShowShortcuts] = useState(false)
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(false)
  const [rightPanelCollapsed, setRightPanelCollapsed] = useState(false)
  const [draggedImage, setDraggedImage] = useState<UploadedFile | null>(null)

  const {
    files: uploadedImages,
    inputProps,
    dropZoneProps,
    openFilePicker,
    removeFile: removeFromLibrary,
    clearFiles,
  } = useFileUpload({
    accept: ["image/*"],
    maxTotalSize: 250 * 1024 * 1024,
  })

  const currentPage = pages[selectedPageIndex]
  const selectedItem = currentPage?.items.find((item) => item.id === selectedItemId) ?? null

  const pageDimensions = useMemo(() => {
    const base = PAGE_DIMENSIONS[pageSize]
    return orientation === "landscape" ? { width: base.height, height: base.width } : base
  }, [pageSize, orientation])

  function createEmptyPage(size: PageSize, orient: Orientation): PageConfig {
    const base = PAGE_DIMENSIONS[size]
    const dims = orient === "landscape" ? { width: base.height, height: base.width } : base
    return { id: generateId(), width: dims.width, height: dims.height, items: [] }
  }

  const pushHistory = () => {
    const state: HistoryState = { pages: JSON.parse(JSON.stringify(pages)), selectedPageIndex }
    setHistory((prev) => [...prev.slice(0, historyIndex + 1), state].slice(-50))
    setHistoryIndex((prev) => prev + 1)
  }

  const undo = () => {
    if (historyIndex > 0) {
      const prevState = history[historyIndex - 1]
      setPages(prevState.pages)
      setSelectedPageIndex(prevState.selectedPageIndex)
      setHistoryIndex((prev) => prev - 1)
      setSelectedItemId(null)
    }
  }

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const nextState = history[historyIndex + 1]
      setPages(nextState.pages)
      setSelectedPageIndex(nextState.selectedPageIndex)
      setHistoryIndex((prev) => prev + 1)
      setSelectedItemId(null)
    }
  }

  const canUndo = historyIndex > 0
  const canRedo = historyIndex < history.length - 1

  const updateAllPageDimensions = (size: PageSize, orient: Orientation) => {
    const base = PAGE_DIMENSIONS[size]
    const dims = orient === "landscape" ? { width: base.height, height: base.width } : base
    setPages((prev) =>
      prev.map((page) => ({ ...page, width: dims.width, height: dims.height }))
    )
  }

  const handlePageSizeChange = (value: PageSize) => {
    setPageSize(value)
    updateAllPageDimensions(value, orientation)
  }

  const handleOrientationChange = (value: Orientation) => {
    setOrientation(value)
    updateAllPageDimensions(pageSize, value)
  }

  const addPage = () => {
    pushHistory()
    const newPage = createEmptyPage(pageSize, orientation)
    setPages((prev) => [...prev, newPage])
    setSelectedPageIndex(pages.length)
    setSelectedItemId(null)
  }

  const deletePage = (index: number) => {
    if (pages.length <= 1) return
    pushHistory()
    setPages((prev) => prev.filter((_, i) => i !== index))
    if (selectedPageIndex >= index && selectedPageIndex > 0) {
      setSelectedPageIndex((prev) => prev - 1)
    }
    setSelectedItemId(null)
  }

  const duplicatePage = (index: number) => {
    pushHistory()
    const page = pages[index]
    const newPage: PageConfig = {
      ...page,
      id: generateId(),
      items: page.items.map((item) => ({ ...item, id: generateId() })),
    }
    setPages((prev) => [...prev.slice(0, index + 1), newPage, ...prev.slice(index + 1)])
    setSelectedPageIndex(index + 1)
    setSelectedItemId(null)
  }

  const addImageToPage = async (file: UploadedFile, position?: Position) => {
    pushHistory()
    const img = new globalThis.Image()
    img.src = file.preview

    await new Promise((resolve) => {
      img.onload = resolve
    })

    const aspectRatio = img.naturalWidth / img.naturalHeight
    const maxWidth = pageDimensions.width - margins * 2
    const maxHeight = pageDimensions.height - margins * 2

    let width = Math.min(img.naturalWidth, maxWidth)
    let height = width / aspectRatio

    if (height > maxHeight) {
      height = maxHeight
      width = height * aspectRatio
    }

    const newItem: ImageItem = {
      id: generateId(),
      src: file.preview,
      fileName: file.name,
      fileSize: file.size,
      originalWidth: img.naturalWidth,
      originalHeight: img.naturalHeight,
      position: position ?? {
        x: margins + (maxWidth - width) / 2,
        y: margins + (maxHeight - height) / 2,
      },
      size: { width, height },
      rotation: 0,
      zIndex: currentPage.items.length,
      opacity: 100,
      flipX: false,
      flipY: false,
    }

    setPages((prev) =>
      prev.map((page, i) =>
        i === selectedPageIndex ? { ...page, items: [...page.items, newItem] } : page
      )
    )
    setSelectedItemId(newItem.id)
  }

  const handleDropImages = async (files: File[], position: Position) => {
    for (const file of files) {
      const preview = URL.createObjectURL(file)
      const uploadedFile: UploadedFile = {
        id: generateId(),
        file,
        preview,
        name: file.name,
        size: file.size,
      }
      await addImageToPage(uploadedFile, position)
    }
  }

  const handleDropFromTray = async (imageId: string, position: Position) => {
    const file = uploadedImages.find((f) => f.id === imageId)
    if (file) {
      await addImageToPage(file, position)
    }
  }

  const updateItem = (id: string, update: Partial<ImageItem>) => {
    setPages((prev) =>
      prev.map((page, i) =>
        i === selectedPageIndex
          ? { ...page, items: page.items.map((item) => (item.id === id ? { ...item, ...update } : item)) }
          : page
      )
    )
  }

  const handleItemPositionChange = (id: string, position: Position) => updateItem(id, { position })

  const handleItemSizeChange = (id: string, size: Size) => updateItem(id, { size })

  const handleItemRotationChange = (id: string, rotation: number) => updateItem(id, { rotation })

  const handleOpacityChange = (id: string, opacity: number) => updateItem(id, { opacity })

  const handleDeleteItem = (id: string) => {
    pushHistory()
    setPages((prev) =>
      prev.map((page, i) =>
        i === selectedPageIndex ? { ...page, items: page.items.filter((item) => item.id !== id) } : page
      )
    )
    setSelectedItemId(null)
  }

  const handleDuplicateItem = (id: string) => {
    pushHistory()
    const item = currentPage?.items.find((it) => it.id === id)
    if (!item) return
    const newItem: ImageItem = {
      ...item,
      id: generateId(),
      position: { x: item.position.x + 20, y: item.position.y + 20 },
      zIndex: currentPage.items.length,
      opacity: item.opacity ?? 100,
      flipX: item.flipX ?? false,
      flipY: item.flipY ?? false,
    }
    setPages((prev) =>
      prev.map((page, i) =>
        i === selectedPageIndex ? { ...page, items: [...page.items, newItem] } : page
      )
    )
    setSelectedItemId(newItem.id)
  }

  const handleBringForward = (id: string) => {
    setPages((prev) =>
      prev.map((page, i) => {
        if (i !== selectedPageIndex) return page
        const maxZ = Math.max(...page.items.map((it) => it.zIndex))
        return { ...page, items: page.items.map((it) => (it.id === id ? { ...it, zIndex: maxZ + 1 } : it)) }
      })
    )
  }

  const handleSendBackward = (id: string) => {
    setPages((prev) =>
      prev.map((page, i) => {
        if (i !== selectedPageIndex) return page
        const minZ = Math.min(...page.items.map((it) => it.zIndex))
        return { ...page, items: page.items.map((it) => (it.id === id ? { ...it, zIndex: minZ - 1 } : it)) }
      })
    )
  }

  const handleBringToFront = (id: string) => {
    setPages((prev) =>
      prev.map((page, i) => {
        if (i !== selectedPageIndex) return page
        const maxZ = Math.max(...page.items.map((it) => it.zIndex))
        return { ...page, items: page.items.map((it) => (it.id === id ? { ...it, zIndex: maxZ + 1 } : it)) }
      })
    )
  }

  const handleSendToBack = (id: string) => {
    setPages((prev) =>
      prev.map((page, i) => {
        if (i !== selectedPageIndex) return page
        const minZ = Math.min(...page.items.map((it) => it.zIndex))
        return { ...page, items: page.items.map((it) => (it.id === id ? { ...it, zIndex: minZ - 1 } : it)) }
      })
    )
  }

  const handleFitToPage = () => {
    if (!selectedItem) return
    pushHistory()
    const contentWidth = pageDimensions.width - margins * 2
    const contentHeight = pageDimensions.height - margins * 2
    const aspectRatio = selectedItem.originalWidth / selectedItem.originalHeight

    let newWidth = contentWidth
    let newHeight = contentWidth / aspectRatio

    if (newHeight > contentHeight) {
      newHeight = contentHeight
      newWidth = contentHeight * aspectRatio
    }

    handleItemSizeChange(selectedItem.id, { width: newWidth, height: newHeight })
    handleItemPositionChange(selectedItem.id, {
      x: margins + (contentWidth - newWidth) / 2,
      y: margins + (contentHeight - newHeight) / 2,
    })
  }

  const handleCenterHorizontal = () => {
    if (!selectedItem) return
    handleItemPositionChange(selectedItem.id, {
      x: (pageDimensions.width - selectedItem.size.width) / 2,
      y: selectedItem.position.y,
    })
  }

  const handleCenterVertical = () => {
    if (!selectedItem) return
    handleItemPositionChange(selectedItem.id, {
      x: selectedItem.position.x,
      y: (pageDimensions.height - selectedItem.size.height) / 2,
    })
  }

  const handleRotate90 = () => {
    if (!selectedItem) return
    handleItemRotationChange(selectedItem.id, (selectedItem.rotation + 90) % 360)
  }

  const handleFlipHorizontal = () => {
    if (!selectedItem) return
    updateItem(selectedItem.id, { flipX: !selectedItem.flipX })
  }

  const handleFlipVertical = () => {
    if (!selectedItem) return
    updateItem(selectedItem.id, { flipY: !selectedItem.flipY })
  }

  const nudgeItem = (dx: number, dy: number) => {
    if (!selectedItem) return
    handleItemPositionChange(selectedItem.id, {
      x: selectedItem.position.x + dx,
      y: selectedItem.position.y + dy,
    })
  }

  const isProcessing = status === "creating-job" || status === "uploading" || status === "processing"

  useKeyboardShortcuts(
    [
      { key: "Delete", handler: () => selectedItemId && handleDeleteItem(selectedItemId) },
      { key: "Backspace", handler: () => selectedItemId && handleDeleteItem(selectedItemId) },
      { key: "Escape", handler: () => setSelectedItemId(null) },
      { key: "ArrowUp", handler: () => nudgeItem(0, -NUDGE_AMOUNT) },
      { key: "ArrowDown", handler: () => nudgeItem(0, NUDGE_AMOUNT) },
      { key: "ArrowLeft", handler: () => nudgeItem(-NUDGE_AMOUNT, 0) },
      { key: "ArrowRight", handler: () => nudgeItem(NUDGE_AMOUNT, 0) },
      { key: "ArrowUp", shift: true, handler: () => nudgeItem(0, -NUDGE_AMOUNT_SHIFT) },
      { key: "ArrowDown", shift: true, handler: () => nudgeItem(0, NUDGE_AMOUNT_SHIFT) },
      { key: "ArrowLeft", shift: true, handler: () => nudgeItem(-NUDGE_AMOUNT_SHIFT, 0) },
      { key: "ArrowRight", shift: true, handler: () => nudgeItem(NUDGE_AMOUNT_SHIFT, 0) },
      { key: "f", handler: handleFitToPage },
      { key: "h", handler: handleCenterHorizontal },
      { key: "v", handler: handleCenterVertical },
      { key: "r", handler: handleRotate90 },
      { key: "d", ctrl: true, handler: () => selectedItemId && handleDuplicateItem(selectedItemId) },
      { key: "]", handler: () => selectedItemId && handleBringForward(selectedItemId) },
      { key: "[", handler: () => selectedItemId && handleSendBackward(selectedItemId) },
      { key: "]", ctrl: true, handler: () => selectedItemId && handleBringToFront(selectedItemId) },
      { key: "[", ctrl: true, handler: () => selectedItemId && handleSendToBack(selectedItemId) },
      { key: "z", ctrl: true, handler: undo },
      { key: "y", ctrl: true, handler: redo },
      { key: "z", ctrl: true, shift: true, handler: redo },
      { key: "=", ctrl: true, handler: () => setCanvasScale((s) => Math.min(2, s + 0.1)) },
      { key: "-", ctrl: true, handler: () => setCanvasScale((s) => Math.max(0.25, s - 0.1)) },
      { key: "0", ctrl: true, handler: () => setCanvasScale(1) },
      { key: "g", handler: () => setShowGrid((s) => !s) },
      { key: "?", handler: () => setShowShortcuts((s) => !s) },
    ],
    !isProcessing
  )

  const totalImages = useMemo(
    () => pages.reduce((sum, page) => sum + page.items.length, 0),
    [pages]
  )

  const generatePdf = async () => {
    if (totalImages === 0) {
      toast.error("Add at least one image to generate PDF")
      return
    }

    setStatus("creating-job")
    setError(null)
    setUploadProgress(0)

    try {
      const allItems = pages.flatMap((page, pageIndex) =>
        page.items.map((item) => ({ pageIndex, item }))
      )

      const uniqueImages = new Map<string, { item: ImageItem; pageIndex: number }>()
      allItems.forEach(({ item, pageIndex }) => {
        if (!uniqueImages.has(item.src)) {
          uniqueImages.set(item.src, { item, pageIndex })
        }
      })

      const imageDataArray = await Promise.all(
        Array.from(uniqueImages.values()).map(async ({ item }) => {
          const response = await fetch(item.src)
          const blob = await response.blob()
          return { item, blob, contentType: blob.type || "image/png" }
        })
      )

      const createResponse = await fetch(`${API_URL}/api/v1/image/to-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": API_KEY,
        },
        body: JSON.stringify({
          imageCount: imageDataArray.length,
          images: imageDataArray.map(({ item, blob, contentType }) => ({
            fileName: item.fileName,
            fileSizeBytes: blob.size,
            contentType,
          })),
          options: {
            outputFileName,
            pageSize,
            orientation: orientation === "portrait" ? "Portrait" : "Landscape",
            includeMargins: margins > 0,
            fitMode: "Custom",
          },
          pages: pages.map((page) => ({
            width: page.width,
            height: page.height,
            items: page.items.map((item) => ({
              fileName: item.fileName,
              x: item.position.x,
              y: item.position.y,
              width: item.size.width,
              height: item.size.height,
              rotation: item.rotation,
            })),
          })),
        }),
      })

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to create conversion job")
      }

      const { jobId, uploadSlots } = (await createResponse.json()) as {
        jobId: string
        uploadSlots: UploadSlot[]
      }

      setStatus("uploading")

      for (let i = 0; i < uploadSlots.length; i++) {
        const slot = uploadSlots[i]
        const imageData = imageDataArray[slot.index]

        const uploadResponse = await fetch(slot.uploadUrl, {
          method: "PUT",
          body: imageData.blob,
          headers: { "Content-Type": imageData.contentType },
        })

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload image ${i + 1}`)
        }

        setUploadProgress(((i + 1) / uploadSlots.length) * 100)
      }

      setStatus("processing")

      const processResponse = await fetch(`${API_URL}/api/v1/image/to-pdf/${jobId}/process`, {
        method: "POST",
        headers: { "X-API-Key": API_KEY },
      })

      if (!processResponse.ok) {
        const errorData = await processResponse.json().catch(() => ({}))
        throw new Error(errorData.detail || "Failed to convert images to PDF")
      }

      const result = (await processResponse.json()) as {
        downloadUrl: string
        fileName: string
        fileSizeBytes: number
        pageCount: number
      }

      setDownloadUrl(result.downloadUrl)
      setResultInfo({ fileName: result.fileName, size: result.fileSizeBytes, pages: result.pageCount })
      setStatus("completed")
      toast.success(`PDF created with ${result.pageCount} pages`)
      window.open(result.downloadUrl, "_blank")
    } catch (err) {
      setStatus("error")
      const message = err instanceof Error ? err.message : "An error occurred"
      setError(message)
      toast.error(message)
    }
  }

  const downloadPdf = () => {
    if (!downloadUrl) return
    window.open(downloadUrl, "_blank")
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-col h-[calc(100vh-12rem)] min-h-[600px] border rounded-lg overflow-hidden bg-background">
        <WorkspaceToolbar
          leftPanelCollapsed={leftPanelCollapsed}
          onToggleLeftPanel={() => setLeftPanelCollapsed(!leftPanelCollapsed)}
          rightPanelCollapsed={rightPanelCollapsed}
          onToggleRightPanel={() => setRightPanelCollapsed(!rightPanelCollapsed)}
          inputProps={inputProps}
          onOpenFilePicker={openFilePicker}
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={undo}
          onRedo={redo}
          canvasScale={canvasScale}
          onZoomIn={() => setCanvasScale((s) => Math.min(2, s + 0.1))}
          onZoomOut={() => setCanvasScale((s) => Math.max(0.25, s - 0.1))}
          onResetZoom={() => setCanvasScale(1)}
          showGrid={showGrid}
          onToggleGrid={() => setShowGrid(!showGrid)}
          isProcessing={isProcessing}
          selectedItem={selectedItem}
          onFitToPage={handleFitToPage}
          onRotate90={handleRotate90}
          onFlipHorizontal={handleFlipHorizontal}
          onFlipVertical={handleFlipVertical}
          onDuplicateItem={handleDuplicateItem}
          onDeleteItem={handleDeleteItem}
          onToggleShortcuts={() => setShowShortcuts(!showShortcuts)}
          status={status}
          uploadProgress={uploadProgress}
          totalImages={totalImages}
          onGeneratePdf={generatePdf}
        />

        <div className="flex flex-1 overflow-hidden">
          <div
            className={cn(
              "border-r flex flex-col bg-muted/20 transition-all duration-200",
              leftPanelCollapsed ? "w-0 border-r-0 overflow-hidden" : "w-52"
            )}
          >
            <ScrollArea className="flex-1">
              <PageList
                pages={pages}
                selectedPageIndex={selectedPageIndex}
                disabled={isProcessing}
                onSelectPage={(i) => {
                  setSelectedPageIndex(i)
                  setSelectedItemId(null)
                }}
                onDeletePage={deletePage}
                onDuplicatePage={duplicatePage}
                onAddPage={addPage}
              />
            </ScrollArea>
          </div>

          <PageCanvas
            pageWidth={pageDimensions.width}
            pageHeight={pageDimensions.height}
            items={currentPage?.items ?? []}
            selectedItemId={selectedItemId}
            scale={canvasScale}
            showGrid={showGrid}
            margins={margins}
            disabled={isProcessing}
            onItemPositionChange={handleItemPositionChange}
            onItemSizeChange={handleItemSizeChange}
            onItemRotationChange={handleItemRotationChange}
            onSelectItem={setSelectedItemId}
            onDeleteItem={handleDeleteItem}
            onDuplicateItem={handleDuplicateItem}
            onBringForward={handleBringForward}
            onSendBackward={handleSendBackward}
            onBringToFront={handleBringToFront}
            onSendToBack={handleSendToBack}
            onFlipHorizontal={(id) => updateItem(id, { flipX: !currentPage?.items.find((it) => it.id === id)?.flipX })}
            onFlipVertical={(id) => updateItem(id, { flipY: !currentPage?.items.find((it) => it.id === id)?.flipY })}
            onResetSize={(id) => {
              const item = currentPage?.items.find((it) => it.id === id)
              if (item) {
                handleItemSizeChange(id, { width: item.originalWidth, height: item.originalHeight })
              }
            }}
            onDropImages={handleDropImages}
            onDropFromTray={handleDropFromTray}
          />

          <div
            className={cn(
              "border-l flex flex-col bg-muted/20 transition-all duration-200",
              rightPanelCollapsed ? "w-0 border-l-0 overflow-hidden" : "w-72"
            )}
          >
            <Tabs defaultValue="images" className="flex-1 flex flex-col">
              <TabsList className="mx-2 mt-2 grid grid-cols-3">
                <TabsTrigger value="images" className="text-xs">
                  <ImageIcon className="h-3.5 w-3.5 mr-1" />
                  Images
                </TabsTrigger>
                <TabsTrigger value="properties" className="text-xs">
                  <Layers className="h-3.5 w-3.5 mr-1" />
                  Edit
                </TabsTrigger>
                <TabsTrigger value="settings" className="text-xs">
                  <Settings2 className="h-3.5 w-3.5 mr-1" />
                  Settings
                </TabsTrigger>
              </TabsList>

              <TabsContent value="images" className="flex-1 overflow-hidden m-0">
                <ScrollArea className="h-full">
                  <div className="p-3 space-y-3">
                    <div
                      {...dropZoneProps}
                      className="border-2 border-dashed rounded-lg p-3 text-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors"
                      onClick={openFilePicker}
                    >
                      <Plus className="h-6 w-6 mx-auto mb-1 text-muted-foreground" />
                      <p className="text-xs font-medium">Drop images or click</p>
                    </div>

                    {uploadedImages.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <p className="text-xs font-medium text-muted-foreground">
                            Drag to canvas ({uploadedImages.length})
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 text-xs text-muted-foreground hover:text-destructive px-1"
                            onClick={clearFiles}
                          >
                            Clear
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5">
                          {uploadedImages.map((file) => (
                            <div
                              key={file.id}
                              draggable
                              onDragStart={(e) => {
                                e.dataTransfer.setData("application/x-image-id", file.id)
                                e.dataTransfer.effectAllowed = "copy"
                                setDraggedImage(file)
                              }}
                              onDragEnd={() => setDraggedImage(null)}
                              className={cn(
                                "group relative aspect-square border rounded-md overflow-hidden cursor-grab active:cursor-grabbing hover:ring-2 hover:ring-primary/50 bg-muted/30 transition-all",
                                draggedImage?.id === file.id && "opacity-50 ring-2 ring-primary"
                              )}
                            >
                              <Image
                                src={file.preview}
                                alt={file.name}
                                fill
                                className="object-cover pointer-events-none"
                                draggable={false}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                              <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <GripVertical className="h-4 w-4 text-white drop-shadow" />
                              </div>
                              <Button
                                variant="destructive"
                                size="icon"
                                className="absolute top-1 right-1 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  removeFromLibrary(file.id)
                                }}
                              >
                                <X className="h-3 w-3" />
                              </Button>
                              <div className="absolute bottom-0 left-0 right-0 text-white text-[10px] px-1.5 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                                {file.name}
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground text-center">
                          Drag images onto the page canvas
                        </p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </TabsContent>

              <TabsContent value="properties" className="flex-1 overflow-hidden m-0">
                <ScrollArea className="h-full">
                  <PropertiesPanel
                    item={selectedItem}
                    pageWidth={pageDimensions.width}
                    pageHeight={pageDimensions.height}
                    margins={margins}
                    disabled={isProcessing}
                    onPositionChange={handleItemPositionChange}
                    onSizeChange={handleItemSizeChange}
                    onRotationChange={handleItemRotationChange}
                    onOpacityChange={handleOpacityChange}
                    onFlipX={(id) => updateItem(id, { flipX: !selectedItem?.flipX })}
                    onFlipY={(id) => updateItem(id, { flipY: !selectedItem?.flipY })}
                    onDelete={handleDeleteItem}
                    onBringForward={handleBringForward}
                    onSendBackward={handleSendBackward}
                  />
                </ScrollArea>
              </TabsContent>

              <TabsContent value="settings" className="flex-1 overflow-hidden m-0">
                <ScrollArea className="h-full">
                  <WorkspaceSettingsTab
                    outputFileName={outputFileName}
                    onOutputFileNameChange={setOutputFileName}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    orientation={orientation}
                    onOrientationChange={handleOrientationChange}
                    margins={margins}
                    onMarginsChange={setMargins}
                    pageCount={pages.length}
                    totalImages={totalImages}
                    pageWidth={pageDimensions.width}
                    pageHeight={pageDimensions.height}
                    disabled={isProcessing}
                  />
                </ScrollArea>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        {isProcessing && status === "uploading" && (
          <div className="px-4 py-2 border-t">
            <Progress value={uploadProgress} />
          </div>
        )}

        {error && (
          <div className="px-4 py-2 border-t bg-destructive/10 flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {status === "completed" && resultInfo && (
          <div className="px-4 py-2 border-t bg-green-50 dark:bg-green-950/20 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 w-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Download className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-200">
                  {resultInfo.fileName} downloaded
                </p>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {formatFileSize(resultInfo.size)} · {resultInfo.pages} {resultInfo.pages === 1 ? "page" : "pages"}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={downloadPdf} className="text-green-700 border-green-300 hover:bg-green-100">
              Download again
            </Button>
          </div>
        )}

        <KeyboardShortcutsDialog open={showShortcuts} onOpenChange={setShowShortcuts} />
      </div>
    </TooltipProvider>
  )
}
